import { v4 as uuidv4, v7 as uuidv7 } from 'uuid';

/**
 * Producer identity for the agent activity event stream (BEAST-13036).
 *
 * Deliberately MODULE STATE, not Redux state: it must never be serialized, rehydrated or shared
 * across windows. AIQ runs several windows behind one shared worker, and each window keeps its own
 * session id and its own sequence - the shared worker must not merge or renumber them.
 *
 * DO NOT reuse the shared worker's window identity for `producer_session_id`. Both candidates fail:
 * `sharedWorker.windowId` is a display ordinal (1, 2, 3, rendered as "Viewing tab 1 of 3") that is
 * reassigned as windows open and close, and `sharedWorker.uid` is a 10-character `ShortUniqueId`
 * token. Neither is a UUID, and the AIQ backend validates `producer_session_id` as `format: uuid`
 * per event - so a non-UUID value there does not degrade anything, it rejects EVERY event we ever
 * emit, forever, while the requests keep answering 202.
 */

/** One per browser window per session, generated lazily so a module import costs nothing. */
let producerSessionId = null;

/**
 * Monotonic per session, assigned when an event is CREATED rather than when it is sent. This is
 * what preserves interaction order downstream, so a queue reorder or a retry must never change it.
 */
let producerSequence = 0;

export function getProducerSessionId() {
    if (!producerSessionId) {
        // v4 rather than `crypto.randomUUID()`: same shape, no secure-context requirement, and it
        // works unchanged under jsdom. The schema only asks for `format: uuid`, not a version.
        producerSessionId = uuidv4();
    }
    return producerSessionId;
}

export function nextProducerSequence() {
    const current = producerSequence;
    producerSequence += 1;
    return current;
}

/**
 * `event_id` is UUIDv7 - v7 specifically, because the store relies on it for time ordering.
 *
 * Generated when the event is created and NEVER at send time: the shared service deduplicates on
 * `event_id`, which is the only reason at-least-once delivery is safe, so a retried batch has to
 * carry byte-identical ids. Must be a real RFC 9562 implementation: a hand-rolled "millis in hex
 * plus random hex" sets neither the version nor the variant nibble and fails the backend's UUID
 * pattern, which would reject every event while the endpoint keeps answering 202.
 */
export function nextEventId() {
    return uuidv7();
}

/** Test seam only: both values are module state that would otherwise leak between tests. */
export function resetActivitySession() {
    producerSessionId = null;
    producerSequence = 0;
}
